/**
 * useHolidayAdmin — Holiday Calendar admin operations (Module 8 Phase 2).
 *
 * Calls the 5 admin callables: createHoliday, updateHoliday, deleteHoliday,
 * listHolidays, bulkImportHolidaysFromExcel. Per-year scoped — pass the
 * `schoolYear` argument and the hook re-fetches when it changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { functions, db } from '../services/firebase';
import { useAuth } from './useAuth';
import { schoolYearVariants } from '@shared/utils/schoolYear';
import { getErrorMessage } from '@shared/utils/errorUtils';
import type {
  Holiday,
  CreateHolidayRequest,
  CreateHolidayResponse,
  UpdateHolidayRequest,
  UpdateHolidayResponse,
  DeleteHolidayRequest,
  DeleteHolidayResponse,
  BulkImportHolidaysRequest,
  BulkImportHolidaysResponse,
} from '../../../shared/types';

export function useHolidayAdmin(schoolYear: string) {
  const { customClaims } = useAuth();
  const schoolId = customClaims?.schoolId;
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * ЖИВ прочит (onSnapshot) вместо еднократно `listHolidays`.
   *
   * Ваканциите се менят и отвън — друг админ, импорт от Excel. Еднократното
   * четене показваше стар списък, без нищо да подскаже, че е остарял
   * (owner, 2026-09-03). Писането остава през callable-ите: там е валидацията
   * (застъпващи се периоди) и одитът.
   *
   * Заявката и подредбата повтарят `listHolidays` едно към едно — разминаване
   * значи, че екранът показва различно според това кой път го е зареждал.
   */
  useEffect(() => {
    if (!schoolYear || !schoolId) {
      setHolidays([]);
      return;
    }
    setLoading(true);
    setError(null);
    const [slash, dash] = schoolYearVariants(schoolYear);
    const unsub = onSnapshot(
      query(
        collection(db, 'schools', schoolId, 'holidays'),
        where('schoolYear', 'in', [slash, dash]),
      ),
      (snap) => {
        setHolidays(
          snap.docs
            .map((d) => d.data() as Holiday)
            .sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis()),
        );
        setLoading(false);
      },
      (err) => {
        setError(getErrorMessage(err) || 'Failed to load holidays');
        setHolidays([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [schoolYear, schoolId]);

  /** Запазен за callers, които искат изрично обновяване — потокът е жив. */
  const fetchHolidays = useCallback(async () => {}, []);

  const createHoliday = useCallback(
    async (data: CreateHolidayRequest) => {
      const fn = httpsCallable<CreateHolidayRequest, CreateHolidayResponse>(
        functions,
        'createHoliday',
      );
      const result = await fn(data);
      await fetchHolidays();
      return result.data;
    },
    [fetchHolidays],
  );

  const updateHoliday = useCallback(
    async (data: UpdateHolidayRequest) => {
      const fn = httpsCallable<UpdateHolidayRequest, UpdateHolidayResponse>(
        functions,
        'updateHoliday',
      );
      const result = await fn(data);
      await fetchHolidays();
      return result.data;
    },
    [fetchHolidays],
  );

  const deleteHoliday = useCallback(
    async (holidayId: string) => {
      const fn = httpsCallable<DeleteHolidayRequest, DeleteHolidayResponse>(
        functions,
        'deleteHoliday',
      );
      const result = await fn({ holidayId });
      await fetchHolidays();
      return result.data;
    },
    [fetchHolidays],
  );

  const bulkImport = useCallback(
    async (data: BulkImportHolidaysRequest) => {
      const fn = httpsCallable<BulkImportHolidaysRequest, BulkImportHolidaysResponse>(
        functions,
        'bulkImportHolidaysFromExcel',
      );
      const result = await fn(data);
      await fetchHolidays();
      return result.data;
    },
    [fetchHolidays],
  );

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  return {
    holidays,
    loading,
    error,
    refetch: fetchHolidays,
    createHoliday,
    updateHoliday,
    deleteHoliday,
    bulkImport,
  };
}
